import React from "react";
import { Box } from "@mui/joy";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { theme } from "styles/theme";

// 에이전트/리뷰어 텍스트(마크다운)를 Joy UI 스타일로 렌더한다. 부모 버블의 font-size /
// color / line-height 를 상속하고, 여기서는 간격·강조만 덮어쓴다.
//
// 보안: react-markdown@10 은 기본값으로 raw HTML 을 렌더하지 않는다(escape). rehype-raw 를
// 켜지 않으므로 별도 sanitizer(dompurify) 가 필요 없다.
// 스트리밍: 매 delta 마다 전체 문자열을 재파싱한다 — 채팅 메시지 크기상 무해. 닫히지 않은
// 토큰(예: 미완성 "**")은 닫힐 때까지 리터럴로 보인다(허용).

const MONO = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

const components: Components = {
  p: ({ children }) => (
    <Box component="p" sx={{ m: 0, "&:not(:first-of-type)": { mt: "3px" } }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: "3px", pl: "1em" }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: "3px", pl: "1em" }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: "1px" }}>
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600 }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box component="em" sx={{ fontStyle: "italic" }}>
      {children}
    </Box>
  ),
  a: ({ children, href }) => (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: theme.vars.palette.primary.solid,
        textDecoration: "underline",
      }}
    >
      {children}
    </Box>
  ),
  code: ({ className, children }) => {
    // 코드펜스(```)는 language-* 클래스를 달고 pre 안에 들어온다 → pre 가 박스를 그리므로
    // 여기선 글꼴만. 그 외 인라인 코드만 칩 스타일.
    const isBlock =
      typeof className === "string" && className.includes("language-");
    if (isBlock) {
      return (
        <Box component="code" sx={{ fontFamily: MONO }}>
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          fontFamily: MONO,
          fontSize: "0.9em",
          px: "4px",
          py: "1px",
          borderRadius: "4px",
          backgroundColor: theme.vars.palette.Background.Level2,
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        my: "4px",
        p: "8px 10px",
        borderRadius: "6px",
        backgroundColor: theme.vars.palette.Background.Level2,
        fontFamily: MONO,
        fontSize: "0.9em",
        // 가로 스크롤 대신 화면 폭에 맞춰 줄바꿈. pre-wrap 으로 코드 내 개행·들여쓰기는
        // 보존하고, overflowWrap 으로 공백 없는 긴 토큰(URL·해시 등)도 강제로 접는다.
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
      }}
    >
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        my: "4px",
        pl: "10px",
        borderLeft: `3px solid ${theme.vars.palette.Border.Weak}`,
        color: theme.vars.palette.Content.Medium,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        my: "6px",
        border: 0,
        borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
      }}
    />
  ),
  // h1–h6 은 버블 안이라 큰 제목이 어색하다 — 살짝 키운 굵은 줄로 통일.
  h1: ({ children }) => <Heading>{children}</Heading>,
  h2: ({ children }) => <Heading>{children}</Heading>,
  h3: ({ children }) => <Heading>{children}</Heading>,
  h4: ({ children }) => <Heading>{children}</Heading>,
  h5: ({ children }) => <Heading>{children}</Heading>,
  h6: ({ children }) => <Heading>{children}</Heading>,
};

function Heading({ children }: { children?: React.ReactNode }) {
  return (
    <Box
      sx={{
        fontWeight: 700,
        fontSize: "1.05em",
        mt: "6px",
        mb: "1px",
        "&:first-of-type": { mt: 0 },
      }}
    >
      {children}
    </Box>
  );
}

// 마크다운 문법으로 보이는 토큰이 하나라도 있으면 true. 원본 보기 토글을 노출할지
// (= 렌더 결과가 원본과 달라 보일 여지가 있는지) 판단하는 휴리스틱이라 느슨해도 무방.
const MARKDOWN_PATTERN =
  /(^|\n)\s{0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s)|\*\*|__|`|\[[^\]]+\]\([^)]+\)|(^|\n)\s*\|.*\|/;

function looksLikeMarkdown(text: string): boolean {
  return MARKDOWN_PATTERN.test(text);
}

// 첫/마지막 블록의 바깥 여백을 제거해 버블 패딩과 겹치지 않게 한다. whiteSpace 를 normal 로
// 명시해 부모 버블의 pre-wrap 이 상속돼 블록 사이 개행이 빈 줄로 렌더되는 것을 막는다(간격 과다 원인).
// lineHeight 는 채팅 본문에 맞춰 살짝 타이트하게.
//
// 마크다운 문법이 감지되면 하단 우측에 원본/렌더 토글을 노출한다 (기본 렌더). 문법이 없으면
// 렌더 결과 == 원본이라 토글을 숨긴다.
//
// rawTogglePullRight: 토글을 콘텐츠 우측 끝보다 더 오른쪽으로 당기는 음수 마진(px 문자열).
// 버블처럼 우측 거터(접기 chevron) 가 있는 컨텍스트에서 토글 우측을 chevron 에 맞출 때 사용.
export function Markdown({
  text,
  rawTogglePullRight,
}: {
  text: string;
  rawTogglePullRight?: string;
}) {
  const [showRaw, setShowRaw] = React.useState(false);
  const hasMarkdown = looksLikeMarkdown(text);

  return (
    <Box
      sx={{
        whiteSpace: "normal",
        lineHeight: 1.5,
        // 인라인 코드·링크·표 셀의 공백 없는 긴 문자열도 가로 스크롤 없이 접히게 한다
        // (overflow-wrap 은 상속되므로 자손 전체에 적용). anywhere 는 min-content 너비까지
        // 줄여 flex 버블이 쪼그라들며 일찍 줄바꿈되므로, 사이징에 영향 없는 break-word 사용.
        overflowWrap: "break-word",
        "& > :first-of-type": { mt: 0 },
        "& > :last-of-type": { mb: 0 },
      }}
    >
      {showRaw ? (
        <Box
          component="pre"
          sx={{
            m: 0,
            fontFamily: "inherit",
            fontSize: "inherit",
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
          }}
        >
          {text}
        </Box>
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {text}
        </ReactMarkdown>
      )}
      {hasMarkdown && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: "4px",
            mr: rawTogglePullRight,
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowRaw((v) => !v);
            }}
            sx={{
              p: 0,
              border: 0,
              background: "none",
              cursor: "pointer",
              fontSize: "11px",
              color: theme.vars.palette.Content.Medium,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showRaw ? "md뷰어" : "원본보기"}
          </Box>
        </Box>
      )}
    </Box>
  );
}
